// frontend/src/context/NotificationContext.js
import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification as deleteNotificationApi,
} from '../services/api';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!user?.token) return;
    try {
      const data = await getNotifications();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
    } catch {
      // silently ignore fetch errors
    }
  }, [user?.token]);

  useEffect(() => {
    if (!user?.token) {
      setNotifications([]);
      setUnreadCount(0);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    fetchNotifications();

    // Every deployed environment (uat + prod) serves the app from the same origin
    // as the socket server, so connect to window.location.origin (https → wss).
    // Only local CRA dev (localhost:3000) needs to target the backend's own port.
    // An explicit REACT_APP_SOCKET_URL always wins.
    const isLocalDev = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const socketUrl =
      process.env.REACT_APP_SOCKET_URL ||
      (isLocalDev ? 'http://localhost:5000' : window.location.origin);
    const socket = io(socketUrl, {
      auth: { token: user.token },
      transports: ['websocket'],
    });

    socket.on('notification:new', (notification) => {
      setNotifications((prev) => [notification, ...prev]);
      setUnreadCount((prev) => prev + 1);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.token, fetchNotifications]);

  const markRead = useCallback(async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (String(n.id) === String(id) ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // ignore
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  }, []);

  const removeNotification = useCallback(async (id) => {
    try {
      const wasUnread = notifications.find((n) => String(n.id) === String(id) && !n.isRead);
      await deleteNotificationApi(id);
      setNotifications((prev) => prev.filter((n) => String(n.id) !== String(id)));
      if (wasUnread) setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // ignore
    }
  }, [notifications]);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markRead, markAllRead, removeNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};
